import { PaneSlot } from "../rail/Rail.styles";
import { OpenPane } from "./definePane";
import {
    Placement,
    ChatChange,
    chatChangesFor,
    closeFrame,
    closePanesOwnedByLeaving,
    collapseStage,
    emptyPlacement,
    expandStage,
    FrameId,
    framesLosingContent,
    getFrameIdInSlot,
    leavingPanes,
    openPane,
    openKeepingStagePane,
    vacateRight,
} from "./paneRules";

// A module store rather than React state in a provider. Every list that can open a pane calls into this, and
// a context value that changed with the placement re-rendered all of them: opening, closing or moving a pane
// cost a page-wide render (traced at 560 ms for a drag release alone). Here a component only re-renders for
// the slice of state it selects, and one that only opens panes selects nothing.
interface PaneStoreState {
    placement: Placement;
    /** Which frame has its discard dialog up, if any. */
    confirming: FrameId | undefined;
}

/** The chat is the rail's other occupant. Connected by PaneHost, since the chat lives in React context. */
export interface ChatConnection {
    setIsOpen: (open: boolean) => void;
    setIsExpanded: (expanded: boolean) => void;
}

type PlacementTransition = (placement: Placement) => Placement;

let state: PaneStoreState = { placement: emptyPlacement, confirming: undefined };
const listeners = new Set<() => void>();

// None of these is rendered, so none of them is part of the state above: the dirty flags are only read when
// something is about to be discarded, and a keystroke in a form must not notify every subscriber.
const hasUnsavedChanges: Record<FrameId, boolean> = { first: false, second: false };
const discardHandlers: Record<FrameId, Set<() => void>> = { first: new Set(), second: new Set() };
// Keyed by refetch, valued by the pane that registered it, if a pane did. A pane that is leaving must not be
// refetched by its own farewell refresh, since what it would refetch may be the very thing that just went.
const refetchers = new Map<() => void, string | undefined>();
let pendingConfirmation: { frame: FrameId; action: () => void } | undefined;
let chat: ChatConnection = { setIsOpen: () => {}, setIsExpanded: () => {} };

// Free when the chat is already so, because React bails out on an unchanged value.
function applyChatChanges(changes: Array<ChatChange>) {
    changes.forEach((change) => (change === "close" ? chat.setIsOpen(false) : chat.setIsExpanded(false)));
}

function setState(next: Partial<PaneStoreState>) {
    state = { ...state, ...next };
    listeners.forEach((listener) => listener());
}

function refetchPageBehindPanes(leaving: Array<OpenPane> = []) {
    const leavingInstanceIds = new Set(leaving.map((pane) => pane.instanceId));
    refetchers.forEach((owner, refetch) => {
        if (owner === undefined || !leavingInstanceIds.has(owner)) {
            refetch();
        }
    });
}

// Every transition takes the owned panes of whatever it removes along with it. Decided here, from the store,
// rather than when the owner unmounts, which StrictMode also does to a pane that is still open.
function withOwnedPanesFollowing(transition: PlacementTransition, placement: Placement): Placement {
    return closePanesOwnedByLeaving(placement, transition(placement));
}

function applyTransition(transition: PlacementTransition) {
    const previous = state.placement;
    const next = withOwnedPanesFollowing(transition, previous);
    if (next === previous) {
        return;
    }

    setState({ placement: next });

    // Decided here rather than on unmount, which StrictMode also does to a pane that is still open.
    const leaving = leavingPanes(previous, next);
    if (leaving.some((pane) => pane.definition.refetchPageWhenClosed)) {
        refetchPageBehindPanes(leaving);
    }
}

function confirmFramesThen(frames: Array<FrameId>, action: () => void) {
    const dirtyFrame = frames.find((frame) => hasUnsavedChanges[frame]);
    if (dirtyFrame) {
        pendingConfirmation = { frame: dirtyFrame, action };
        setState({ confirming: dirtyFrame });
        return;
    }
    action();
}

function applyConfirmed(transition: PlacementTransition, afterwards?: () => void) {
    const losing = framesLosingContent(state.placement, withOwnedPanesFollowing(transition, state.placement));
    confirmFramesThen(losing, () => {
        // The pane on show is discarded along with anything typed into it, so its dirty flag must not follow
        // the fresh form and have it ask about edits nobody made.
        losing.forEach((frame) => (hasUnsavedChanges[frame] = false));
        applyTransition(transition);
        afterwards?.();
    });
}

function openMakingRoom(pane: OpenPane, transition: PlacementTransition) {
    const chatChanges = chatChangesFor(pane.definition.slot, state.placement, transition(state.placement));
    applyConfirmed(transition, () => applyChatChanges(chatChanges));
}

function closeFrameNow(frame: FrameId) {
    hasUnsavedChanges[frame] = false;
    applyTransition((placement) => closeFrame(placement, frame));
    if (state.confirming !== undefined) {
        setState({ confirming: undefined });
    }
}

// Asks about the panes the closing one owns as well, since they leave with it. What follows the close runs only once
// the pane has actually gone, so a cancelled discard leaves the user where they were.
function requestCloseFrameThen(frame: FrameId, afterwards?: () => void) {
    applyConfirmed((placement) => closeFrame(placement, frame), afterwards);
}

function clearPendingConfirmation() {
    pendingConfirmation = undefined;
    setState({ confirming: undefined });
}

export const paneStore = {
    subscribe: (listener: () => void) => {
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
        };
    },
    getState: () => state,
    connectChat: (connection: ChatConnection) => {
        chat = connection;
    },

    open: (pane: OpenPane) => openMakingRoom(pane, (placement) => openPane(placement, pane)),
    openBeside: (pane: OpenPane) => openMakingRoom(pane, (placement) => openKeepingStagePane(placement, pane)),

    // Closes without asking. For after a save, or once the user has confirmed.
    closeFrame: closeFrameNow,
    closeSlot: (slot: PaneSlot) => {
        const frame = getFrameIdInSlot(state.placement, slot);
        if (frame !== undefined) {
            closeFrameNow(frame);
        }
    },
    // What the close button and Cancel call: asks first when there is something to lose.
    requestCloseFrame: (frame: FrameId) => requestCloseFrameThen(frame),
    // For a close the user starts by leaving, such as following a link: the leaving happens only if the pane goes.
    requestCloseFrameThen,
    requestCloseSlot: (slot: PaneSlot) => {
        const frame = getFrameIdInSlot(state.placement, slot);
        if (frame !== undefined) {
            requestCloseFrameThen(frame);
        }
    },

    // The stage pane moving into the rail takes it from the chat the same way a right pane arriving would.
    collapseStage: () => {
        applyChatChanges(chatChangesFor("right", state.placement, collapseStage(state.placement)));
        applyTransition(collapseStage);
    },
    expandStage: () => applyTransition(expandStage),
    // Treated like a right pane arriving: a stage pane in the rail goes home while it can, and only what is
    // actually lost is asked about.
    vacateRightThen: (action: () => void) => applyConfirmed(vacateRight, action),

    confirmDiscard: () => {
        const confirmation = pendingConfirmation;
        clearPendingConfirmation();
        if (confirmation) {
            // Before the action, because it is what takes the pane away.
            discardHandlers[confirmation.frame].forEach((onDiscard) => onDiscard());
            confirmation.action();
        }
    },
    cancelDiscard: clearPendingConfirmation,

    setHasUnsavedChanges: (frame: FrameId, value: boolean) => (hasUnsavedChanges[frame] = value),
    // Unmounting cannot stand in for a discard: a pane also unmounts when it closes without asking, after a
    // save, and a draft kept outside React must only go when the user has chosen to throw it away.
    registerDiscard: (frame: FrameId, onDiscard: () => void) => {
        discardHandlers[frame].add(onDiscard);
        return () => {
            discardHandlers[frame].delete(onDiscard);
        };
    },

    // The panes live above the router, so they have no onSave prop to call the way the dialogs they replaced
    // did. The page behind them registers its own refetch instead, and a save reaches back through this.
    registerRefetch: (refetch: () => void, ownerInstanceId: string | undefined) => {
        refetchers.set(refetch, ownerInstanceId);
        return () => {
            refetchers.delete(refetch);
        };
    },
    refetchPageBehindPanes: () => refetchPageBehindPanes(),
};
